import { useEffect, useState } from 'react';
import { fmtDateTime } from '@/lib/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle, Check, Download, Edit3,
  FileWarning, Loader2, Lock, LockOpen, MessageSquarePlus, RefreshCw, RotateCw, ShieldAlert,
  UnlockKeyhole, X,
} from 'lucide-react';
import {
  binder, documentTypes, ApiError,
  type DocumentPreviewResponse,
} from '@/api/client';
import { humanRole } from '@/lib/humanise';
import { useAuth } from '@/contexts/AuthContext';
import { useEisfPermissions } from '@/hooks/useEisfPermissions';
import { useMyPermissions } from '@/hooks/useMyPermissions';
import { CAN_RAISE_QUERY } from '@/lib/permissions';
import { ApprovalProgress } from '@/pages/binder/components/ApprovalProgress';
import { VersionStatusActions } from '@/pages/binder/components/VersionStatusActions';
import { SignApprovalDialog } from '@/pages/binder/components/SignApprovalDialog';
import { AnnotatablePdfView } from '@/pages/binder/components/AnnotatablePdfView';
import { RaiseFolderQueryDialog } from '@/pages/binder/components/RaiseFolderQueryDialog';
import { errorText } from '@/lib/permissionError';

/**
 * Sprint-18 — Document viewer modal.
 *
 * Triggered from any document name click in the binder UI. Layout:
 *   - Modal overlay (full-screen, dark backdrop)
 *   - Header: close X + breadcrumb title + version badge + version nav
 *   - Body LEFT: PDF preview via native browser <iframe> (zoom + page
 *     nav supplied by the browser's PDF viewer)
 *   - Body RIGHT: Information panel (general + details sections)
 *   - Footer: download + close
 *
 * Branches on `preview_status` returned by the BE:
 *   - not_needed / rendered -> render the PDF in the iframe
 *   - pending               -> spinner + auto-poll every 5s
 *   - failed                -> "Preview unavailable" + download fallback
 *
 * PDF rendering uses the browser's native PDF viewer (Chromium /
 * Firefox / Safari all ship one). No react-pdf / pdfjs dependency —
 * keeps the bundle slim and lets the user use the native zoom / search
 * / print without us re-implementing them.
 */

interface DocumentViewerModalProps {
  binderRef: string;
  documentRef: string;
  /** Optional — defaults to the slot's current_version_id, then most recent. */
  versionId?: string;
  /** Punch-list f3 — when supplied, monitors/auditors get a "Raise Query"
   *  action so an external query can be opened in context from this
   *  document and stay linked back to it via document_id. Omitted callers
   *  (e.g. My Files, search results) simply don't show the action. */
  studyId?: string;
  open: boolean;
  onClose: () => void;
}

const POLL_INTERVAL_MS = 5000;
const PRESIGN_EXPIRY_BUFFER_MS = 60_000;

export function DocumentViewerModal({
  binderRef,
  documentRef,
  versionId,
  studyId,
  open,
  onClose,
}: DocumentViewerModalProps) {
  const qc = useQueryClient();
  const [activeVersionId, setActiveVersionId] = useState<string | undefined>(versionId);

  // Sync the active version when the prop changes (e.g. user clicks a
  // different document while the modal is already open).
  useEffect(() => {
    setActiveVersionId(versionId);
  }, [versionId, documentRef]);

  const q = useQuery({
    queryKey: ['document-preview', binderRef, documentRef, activeVersionId ?? 'latest'],
    queryFn: () => binder.getDocumentPreview(binderRef, documentRef, activeVersionId),
    enabled: open,
    // Re-poll while preview_status === 'pending'. tanstack v5 accepts a
    // function returning the interval (or false to stop).
    //
    // Deliberately NOT polled for general status changes (draft ->
    // in_approval -> current) — this query returns a freshly presigned PDF
    // URL every call, so a blanket interval here reloads the iframe (visible
    // flicker, lost scroll/zoom position) on every tick even when nothing
    // changed. Status changes are instead pushed as ONE targeted
    // invalidation exactly when they happen: InitiateApprovalDialog
    // invalidates this query on successful send, and useDocusealSelfHeal
    // (via ApprovalProgress) invalidates it only when it actually detects a
    // completed signature — never on a bare timer tick.
    refetchInterval: (query) => {
      const d = query.state.data as DocumentPreviewResponse | undefined;
      return d?.preview_status === 'pending' ? POLL_INTERVAL_MS : false;
    },
    staleTime: 0,
  });

  // Esc-to-close. Bound at the document level so it works regardless of
  // focus (focus is inside the iframe most of the time).
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Body scroll lock while modal is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Sprint-18 — permissions + check-out/in + metadata edit + tags.
  const { can } = useEisfPermissions();
  const { user } = useAuth();
  const [editOpen, setEditOpen] = useState(false);

  // "Sign" should only be actionable by the approver whose turn it actually
  // is — not every viewer of an in_approval document. Shares the same query
  // key as <ApprovalProgress> below, so this doesn't cost a second request.
  const versionStatusForRouting = q.data?.version.status;
  const routingQ = useQuery({
    queryKey: ['approval-routing', binderRef, documentRef, q.data?.version.version_id],
    queryFn: () => binder.getApprovalRouting(binderRef, documentRef, q.data!.version.version_id),
    enabled: open && !!q.data && versionStatusForRouting === 'in_approval',
  });
  const isMyTurnToSign = routingQ.data?.approvers.some(
    (a) => a.user_id === user?.id && a.status === 'pending',
  ) ?? false;

  const invalidate = () => qc.invalidateQueries({ queryKey: ['document-preview', binderRef, documentRef] });

  // Sprint-18 — when /check-out 409s (another user holds it), open a
  // confirmation that offers force-release (coordinator+ only). The
  // dialog's "Confirm force-release" calls /check-in with force=true.
  const [conflictHolder, setConflictHolder] = useState<string | null>(null);
  // Sprint-18 — DocuSeal envelope expiration recovery dialog.
  const [restartOpen, setRestartOpen] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  // Punch-list f3 — raise a query in context from this document.
  const [raiseQueryOpen, setRaiseQueryOpen] = useState(false);
  // RBAC grid decides (queries.create, 23 Oct 2026).
  const canRaiseQuery = useMyPermissions().holdsOperation('queries.create', CAN_RAISE_QUERY);

  const checkOut = useMutation({
    mutationFn: () => binder.checkOutDocument(binderRef, documentRef),
    onSuccess: () => { toast.success('Checked out — only you can upload new versions until you check in.'); invalidate(); },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        // Server's message already includes the holder's name + remediation hint.
        setConflictHolder(err.message);
      } else {
        toast.error(errorText(err, 'Check-out failed'));
      }
    },
  });

  const checkIn = useMutation({
    mutationFn: (force: boolean) => binder.checkInDocument(binderRef, documentRef, force),
    onSuccess: () => {
      toast.success('Released check-out — slot is now free.');
      setConflictHolder(null);
      invalidate();
    },
    onError: (err) => toast.error(errorText(err, 'Check-in failed')),
  });

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-ink-primary/40 flex items-start justify-center"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-white mt-12 w-[1500px] max-w-[calc(100vw-50px)] h-[calc(100vh-100px)] rounded-md shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Header
          data={q.data}
          loading={q.isLoading}
          error={q.error instanceof ApiError ? q.error : null}
          onClose={onClose}
        />
        <div className="flex-1 flex overflow-hidden">
          <DocPane
            data={q.data}
            loading={q.isLoading}
            onRefresh={() => qc.invalidateQueries({ queryKey: ['document-preview', binderRef, documentRef] })}
            binderRef={binderRef}
            documentRef={documentRef}
          />
          <InfoPane data={q.data} binderRef={binderRef} documentRef={documentRef} onSaved={invalidate} />
        </div>
        <Footer
          data={q.data}
          binderRef={binderRef}
          onClose={onClose}
          can={can}
          onCheckOut={() => checkOut.mutate()}
          onCheckIn={(force) => checkIn.mutate(force)}
          checkOutPending={checkOut.isPending}
          checkInPending={checkIn.isPending}
          onOpenRestart={() => setRestartOpen(true)}
          onOpenSign={() => setSignOpen(true)}
          canSignNow={isMyTurnToSign}
          canRaiseQuery={canRaiseQuery && !!studyId}
          onRaiseQuery={() => setRaiseQueryOpen(true)}
        />
      </div>

      {editOpen && q.data && (
        <EditMetadataDialog
          binderRef={binderRef}
          documentRef={documentRef}
          initial={{
            title: q.data.document.title,
            expires_at: q.data.document.expires_at,
            phi_present: q.data.document.phi_present,
          }}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); invalidate(); }}
        />
      )}

      {conflictHolder && (
        <CheckOutConflictDialog
          message={conflictHolder}
          canForce={can('lock_or_unlock_document')}
          onForce={() => checkIn.mutate(true)}
          forcePending={checkIn.isPending}
          onClose={() => setConflictHolder(null)}
        />
      )}

      {restartOpen && q.data && (
        <RestartSigningDialog
          binderRef={binderRef}
          documentRef={documentRef}
          versionId={q.data.version.version_id}
          onClose={() => setRestartOpen(false)}
          onRestarted={() => { setRestartOpen(false); invalidate(); }}
        />
      )}

      {signOpen && q.data && (
        <SignApprovalDialog
          versionId={q.data.version.version_id}
          versionLabel={q.data.version.version_label}
          documentTitle={q.data.document.title || q.data.document.document_type_name}
          // workflow_applied isn't in the preview response's version shape;
          // fall back to null and let the modal header omit it.
          workflowName={(q.data.version as { workflow_applied?: string | null }).workflow_applied ?? null}
          open={signOpen}
          onClose={() => { setSignOpen(false); invalidate(); }}
          onSigned={() => invalidate()}
        />
      )}

      {raiseQueryOpen && q.data && studyId && (
        // Punch-list f3 — external queries open in context from a binder
        // document; document_id links the query back to it (RaiseFolderQueryDialog
        // passes it through to compliance.raiseQuery).
        <RaiseFolderQueryDialog
          folder={{
            folder_id: q.data.document.folder_id,
            section_name: q.data.document.folder_breadcrumb || '',
            zone_name: '',
            display_name_override: q.data.document.title || null,
            subartifact_label: null,
            artifact_type: '',
          }}
          studyId={studyId}
          documentId={q.data.document.document_id}
          onClose={() => setRaiseQueryOpen(false)}
        />
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------- */

function Header({
  data, loading, error, onClose,
}: {
  data: DocumentPreviewResponse | undefined;
  loading: boolean;
  error: ApiError | null;
  onClose: () => void;
}) {
  return (
    <header className="flex items-center gap-3 px-5 py-3 border-b border-surface-border">
      <button
        type="button"
        onClick={onClose}
        className="p-1.5 text-ink-muted hover:text-ink-primary"
        title="Close"
      >
        <X className="h-5 w-5" />
      </button>
      <div className="flex items-center gap-2 min-w-0 text-base text-ink-primary">
        {loading ? (
          <span className="text-ink-muted text-sm">Loading…</span>
        ) : error ? (
          <span className="text-danger-700 text-sm flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4" /> {error.message}
          </span>
        ) : data ? (
          <>
            <span className="text-ink-muted truncate max-w-md" title={data.document.folder_breadcrumb}>
              {data.document.folder_breadcrumb || '—'} /
            </span>
            <span className="font-medium truncate" title={data.document.title}>
              {data.document.title}
            </span>
            <span className="font-mono text-xs text-ink-muted">
              ({data.document.reference_number ?? data.document.document_id.slice(0, 8)} · {data.version.version_label})
            </span>
            <StatusBadge status={data.document.status} />
            {data.document.is_locked && (
              <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-700">
                <Lock className="h-2.5 w-2.5" /> Locked
              </span>
            )}
            {data.document.phi_present && (
              <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-800">
                <ShieldAlert className="h-2.5 w-2.5" /> PHI
              </span>
            )}
          </>
        ) : null}
      </div>
    </header>
  );
}

/* ----------------------------------------------------------------------- */

function DocPane({
  data, loading, onRefresh, binderRef, documentRef,
}: {
  data: DocumentPreviewResponse | undefined;
  loading: boolean;
  onRefresh: () => void;
  binderRef: string;
  documentRef: string;
}) {
  const { can } = useEisfPermissions();
  return (
    <div className="flex-1 bg-surface-muted/30 border-r border-surface-border overflow-hidden flex items-center justify-center">
      {loading && !data ? (
        <div className="flex items-center gap-2 text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading document…
        </div>
      ) : !data ? (
        <div className="text-ink-muted text-sm">Unable to load document.</div>
      ) : data.preview_status === 'pending' ? (
        <PendingState onRefresh={onRefresh} />
      ) : data.preview_status === 'failed' ? (
        <FailedState download={data.presigned_get_url} fileName={data.version.file_name} />
      ) : data.preview_url ? (
        // Punch-list b14 — pdf.js canvas render + highlight/note overlay,
        // replacing the native-browser iframe (no annotation surface
        // possible on a plain iframe). `key` forces a full remount when
        // the version or presign URL changes.
        <AnnotatablePdfView
          key={`${data.version.version_id}:${data.preview_url}`}
          binderRef={binderRef}
          documentRef={documentRef}
          versionId={data.version.version_id}
          previewUrl={data.preview_url}
          fileName={data.version.file_name}
          locked={!!data.version.signed_at}
          canAnnotate={can('annotate_document')}
        />
      ) : (
        <FallbackDownload download={data.presigned_get_url} fileName={data.version.file_name} />
      )}
    </div>
  );
}

function PendingState({ onRefresh }: { onRefresh: () => void }) {
  return (
    <div className="text-center max-w-sm">
      <Loader2 className="h-8 w-8 animate-spin text-accent-500 mx-auto" />
      <div className="mt-4 text-sm font-medium text-ink-primary">Generating preview…</div>
      <div className="mt-1.5 text-xs text-ink-muted">
        The original is a Word document; we're converting it to PDF for inline display.
        This usually takes 10–30 seconds.
      </div>
      <button
        type="button"
        onClick={onRefresh}
        className="mt-4 inline-flex items-center gap-1.5 text-xs text-accent-700 hover:text-accent-800"
      >
        <RefreshCw className="h-3 w-3" /> Check now
      </button>
    </div>
  );
}

function FailedState({ download, fileName }: { download: string; fileName: string }) {
  return (
    <div className="text-center max-w-sm">
      <FileWarning className="h-8 w-8 text-amber-600 mx-auto" />
      <div className="mt-4 text-sm font-medium text-ink-primary">Preview unavailable</div>
      <div className="mt-1.5 text-xs text-ink-muted">
        We couldn't convert this document to a PDF preview. The original file is still
        available — download it to view in Word or another editor.
      </div>
      <a
        href={download}
        download={fileName}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 inline-flex items-center gap-1.5 rounded bg-ink-primary px-3 h-9 text-sm font-medium text-white hover:bg-ink-primary/90"
      >
        <Download className="h-3.5 w-3.5" /> Download original
      </a>
    </div>
  );
}

function FallbackDownload({ download, fileName }: { download: string; fileName: string }) {
  return (
    <div className="text-center max-w-sm">
      <Download className="h-8 w-8 text-ink-muted mx-auto" />
      <div className="mt-4 text-sm text-ink-primary">This file type isn't previewable inline.</div>
      <a
        href={download}
        download={fileName}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 inline-flex items-center gap-1.5 rounded bg-ink-primary px-3 h-9 text-sm font-medium text-white hover:bg-ink-primary/90"
      >
        <Download className="h-3.5 w-3.5" /> Download
      </a>
    </div>
  );
}

/* ----------------------------------------------------------------------- */

function InfoPane({
  data, binderRef,
}: {
  data: DocumentPreviewResponse | undefined;
  binderRef: string;
  documentRef: string;
  onSaved: () => void;
}) {
  // Sprint-18 Item #7 — signature-required signal. Look up the document
  // type's approver config so we can show "Signature required: Yes/No"
  // alongside the other metadata KVs. Cached for 5 minutes — type
  // approver config rarely changes mid-session.
  //
  // BinderDocumentRow only carries `document_type_name` (natural key on
  // the slot table). The approvers endpoint expects the CODE — passing
  // a name with `/` (e.g. "ISF/eISF Index") makes HttpApi reject the
  // route because `%2F` isn't allowed in a path param. Resolve name →
  // code via the doc-types list, cached 10min.
  const allDocTypesQ = useQuery({
    queryKey: ['document-types', 'list'],
    queryFn: () => documentTypes.list(),
    staleTime: 10 * 60_000,
    enabled: !!data?.document.document_type_name,
  });
  const documentTypeCode = allDocTypesQ.data?.document_types.find(
    (t) => t.document_type_name === data?.document.document_type_name,
  )?.document_type_code ?? null;
  const approversQ = useQuery({
    queryKey: ['document-type-approvers', documentTypeCode],
    queryFn: () => documentTypes.getApprovers(documentTypeCode!),
    enabled: !!documentTypeCode,
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (!data) {
    return (
      <aside className="w-[400px] shrink-0 bg-white p-5 text-sm text-ink-muted">
        Loading metadata…
      </aside>
    );
  }
  return (
    <aside className="w-[400px] shrink-0 bg-white overflow-y-auto">
      <div className="px-5 py-4 border-b border-surface-border">
        <div className="text-base font-semibold text-ink-primary">Information</div>
      </div>
      <div className="px-5 py-2">
        <SectionTitle>General</SectionTitle>
        <KV k="File Name" v={data.version.file_name || '—'} />
        <KV k="Uploaded By" v={data.version.uploaded_by_name || '—'} />
        <KV k="Uploaded" v={fmtDate(data.version.uploaded_at)} />
        <KV k="Document Type" v={data.document.document_type_name || '—'} />
        <KV k="Status" v={<StatusBadge status={data.document.status} />} />
        <KV k="Version" v={`${data.version.version_label} (v${data.version.version_number})`} />
        {data.version.signed_at && <KV k="Signed at" v={fmtDate(data.version.signed_at)} />}

        <SectionTitle>Document</SectionTitle>
        <KV k="Reference #" v={<span className="font-mono">{data.document.reference_number ?? '—'}</span>} />
        <KV k="Title" v={data.document.title} />
        {/* Sprint-18 Item #7 — at-a-glance signature requirement, derived
            from the document type's approver config. Spec: "metadata
            should show signature required to track the status of a doc
            if signature is included." */}
        <KV
          k="Signature required"
          v={(() => {
            if (approversQ.isLoading) {
              return <span className="text-xs text-ink-muted">Looking up…</span>;
            }
            const approvers = approversQ.data?.approvers ?? [];
            if (approvers.length === 0) {
              return (
                <span className="inline-flex rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-700">
                  No — informational document
                </span>
              );
            }
            const roleList = approvers
              .map((a) => humanRole(a.role))
              .join(approversQ.data?.approval_mode === 'sequential' ? ' → ' : ', ');
            return (
              <span>
                <span className="inline-flex rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700">
                  Yes
                </span>
                <span className="ml-2 text-[11px] text-ink-muted">{roleList}</span>
              </span>
            );
          })()}
        />
        {data.document.expires_at && <KV k="Expires" v={fmtDate(data.document.expires_at)} />}
        {data.document.is_locked && (
          <KV
            k="Lock"
            v={
              <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-700">
                <Lock className="h-2.5 w-2.5" /> Locked
              </span>
            }
          />
        )}

        {/* Sprint-18 — live multi-approver routing chain. Polls every 15s
            via ApprovalProgress (Sprint-15 component). Only renders when
            the version is in approval; for terminal versions the routing
            is read-only history accessible via the audit log instead. */}
        {data.version.status === 'in_approval' && (
          <>
            <SectionTitle>Approval workflow</SectionTitle>
            <div className="py-2">
              <ApprovalProgress
                binderId={binderRef}
                documentId={data.document.document_id}
                versionId={data.version.version_id}
              />
            </div>
          </>
        )}

      </div>
    </aside>
  );
}

/**
 * Sprint-18 — Metadata edit dialog. Title, expires_at, phi_present. Server
 * audits the diff via DocumentMetadataChanged.
 */
function EditMetadataDialog({
  binderRef, documentRef, initial, onClose, onSaved,
}: {
  binderRef: string;
  documentRef: string;
  initial: { title: string; expires_at: string | null; phi_present: boolean };
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(initial.title);
  const [expiresAt, setExpiresAt] = useState(initial.expires_at ?? '');
  const [phiPresent, setPhiPresent] = useState(initial.phi_present);

  const save = useMutation({
    mutationFn: () => {
      const patch: Record<string, unknown> = {};
      if (title.trim() !== initial.title) patch.title = title.trim();
      if ((expiresAt || null) !== initial.expires_at) {
        patch.expires_at = expiresAt ? new Date(expiresAt).toISOString() : null;
      }
      if (phiPresent !== initial.phi_present) patch.phi_present = phiPresent;
      return binder.updateDocumentMetadata(binderRef, documentRef, patch);
    },
    onSuccess: (r) => {
      if (r.changed.length === 0) {
        toast.info('No changes to save.');
        onClose();
        return;
      }
      toast.success(`Saved — changed: ${r.changed.join(', ')}`);
      onSaved();
    },
    onError: (err) => toast.error(errorText(err, 'Failed to save metadata')),
  });

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-ink-primary/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-lg p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="text-base font-semibold text-ink-primary flex items-center gap-2">
            <Edit3 className="h-4 w-4" /> Edit document metadata
          </div>
          <button type="button" onClick={onClose} className="text-ink-muted hover:text-ink-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div>
          <label className="label text-xs">Title</label>
          <input
            className="input"
            value={title}
            maxLength={500}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div>
          <label className="label text-xs">Expiry date</label>
          <input
            className="input"
            type="datetime-local"
            value={expiresAt ? expiresAt.slice(0, 16) : ''}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
          <p className="mt-1 text-[11px] text-ink-muted">
            Triggers expiration warnings in the binder UI. Leave blank for no expiry.
          </p>
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={phiPresent}
            onChange={(e) => setPhiPresent(e.target.checked)}
          />
          <ShieldAlert className="h-3.5 w-3.5 text-amber-700" />
          Contains PHI (affects external-reviewer redaction)
        </label>

        <div className="flex justify-end gap-2 pt-2 border-t border-surface-border">
          <button type="button" onClick={onClose} className="btn-ghost" disabled={save.isPending}>Cancel</button>
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="inline-flex items-center gap-1.5 rounded bg-ink-primary px-3.5 h-9 text-sm font-medium text-white hover:bg-ink-primary/90 disabled:opacity-60"
          >
            {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <div className="text-base font-medium text-ink-primary mt-4 mb-1">{children}</div>;
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-start py-2 border-b border-surface-border/50 last:border-0 text-sm">
      <span className="w-[150px] shrink-0 text-ink-secondary">{k}</span>
      <span className="flex-1 text-ink-primary break-words">{v}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === 'current'
      ? 'bg-emerald-50 text-emerald-700'
      : status === 'draft'
        ? 'bg-slate-100 text-slate-700'
        : status === 'in_approval' || status === 'preview'
          ? 'bg-amber-50 text-amber-800'
          : status === 'superseded' || status === 'archived'
            ? 'bg-slate-50 text-slate-500'
            : 'bg-slate-100 text-slate-700';
  return (
    <span className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-medium capitalize ${cls}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

/* ----------------------------------------------------------------------- */

function Footer({
  data, binderRef, onClose, can, onCheckOut, onCheckIn, checkOutPending, checkInPending, onOpenRestart, onOpenSign, canSignNow, canRaiseQuery, onRaiseQuery,
}: {
  data: DocumentPreviewResponse | undefined;
  binderRef: string;
  onClose: () => void;
  can: (op: import('@/api/client').EisfOperation) => boolean;
  onCheckOut: () => void;
  onCheckIn: (force: boolean) => void;
  checkOutPending: boolean;
  checkInPending: boolean;
  onOpenRestart: () => void;
  onOpenSign: () => void;
  /** True only when the signed-in user is the pending approver for this
   *  version right now — "Sign" is hidden from everyone else, since clicking
   *  it would just fail the OTP step (the code always goes to the real
   *  approver's email regardless of who's viewing). */
  canSignNow: boolean;
  /** Punch-list f3 — role-gated (monitor/auditor) AND requires the caller
   *  to have supplied a studyId. */
  canRaiseQuery: boolean;
  onRaiseQuery: () => void;
}) {
  // Sprint-18 — check-out actions, gated by the spec's permission matrix.
  // Buttons hidden when the role can't perform the op, disabled when the
  // doc state blocks the action (e.g. already checked out by someone else).
  const docData = data?.document;
  const isCheckedOut = !!(docData as Record<string, unknown> | undefined)?.checked_out_by_user_id;
  const canCheckOut = can('checkout_document');
  // Sprint-18 — restart-signing visible while version is in an in-flight
  // approval. Operator decides when to declare the envelope dead.
  const versionStatus = data?.version.status;
  const canRestart = can('start_approval_workflow')
    && (versionStatus === 'in_approval' || versionStatus === 'preview');
  return (
    <footer className="px-5 py-3 border-t border-surface-border flex items-center justify-end gap-2 bg-white">
      {data && (
        <VersionStatusActions
          binderId={binderRef}
          documentId={data.document.document_id}
          // Preview API returns a narrower version shape than the full
          // DocumentVersionRow. VersionStatusActions only reads status,
          // version_id, version_label, signed_at, docuseal_submission_id;
          // the first four exist on the preview shape, docuseal_submission_id
          // is null on the preview shape (no envelope-in-flight badge, which
          // is fine — the sidebar's ApprovalProgress panel is the source of
          // truth for in-flight approvals).
          version={{
            ...data.version,
            docuseal_submission_id: null,
          } as unknown as import('@/api/client').DocumentVersionRow}
          isDocLocked={!!data.document.is_locked}
          documentTypeName={data.document.document_type_name}
        />
      )}
      {data && versionStatus === 'in_approval' && canSignNow && (
        <button
          type="button"
          onClick={onOpenSign}
          className="inline-flex items-center gap-1.5 rounded bg-emerald-600 px-3.5 h-9 text-sm font-medium text-white hover:bg-emerald-700"
          title="Sign this document. Verify identity by email code, then sign inside the app."
        >
          Sign
        </button>
      )}
      {canRestart && (
        <button
          type="button"
          onClick={onOpenRestart}
          className="inline-flex items-center gap-1.5 rounded border border-amber-300 bg-amber-50 px-3 h-9 text-sm font-medium text-amber-900 hover:bg-amber-100"
          title="Restart the signing workflow if the DocuSeal envelope expired or got stuck"
        >
          <RotateCw className="h-3.5 w-3.5" />
          Restart signing
        </button>
      )}
      {data && canCheckOut && !data.document.is_locked && (
        isCheckedOut ? (
          <button
            type="button"
            onClick={() => onCheckIn(false)}
            disabled={checkInPending}
            className="inline-flex items-center gap-1.5 rounded border border-surface-border bg-white px-3 h-9 text-sm font-medium text-ink-primary hover:bg-surface-muted/50 disabled:opacity-60"
            title="Release the check-out so others can upload"
          >
            {checkInPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UnlockKeyhole className="h-3.5 w-3.5" />}
            Check in
          </button>
        ) : (
          <button
            type="button"
            onClick={onCheckOut}
            disabled={checkOutPending}
            className="inline-flex items-center gap-1.5 rounded border border-surface-border bg-white px-3 h-9 text-sm font-medium text-ink-primary hover:bg-surface-muted/50 disabled:opacity-60"
            title="Claim a soft-lock so only you can upload new versions"
          >
            {checkOutPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LockOpen className="h-3.5 w-3.5" />}
            Check out
          </button>
        )
      )}
      {data && canRaiseQuery && (
        <button
          type="button"
          onClick={onRaiseQuery}
          className="inline-flex items-center gap-1.5 rounded border border-surface-border bg-white px-3.5 h-9 text-sm font-medium text-ink-primary hover:bg-surface-muted/50"
          title="Raise a query against this document"
        >
          <MessageSquarePlus className="h-3.5 w-3.5" />
          Raise Query
        </button>
      )}
      {data && (
        <a
          href={data.presigned_get_url}
          download={data.version.file_name}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded border border-surface-border bg-white px-3.5 h-9 text-sm font-medium text-ink-primary hover:bg-surface-muted/50"
        >
          <Download className="h-3.5 w-3.5" />
          Download original
        </a>
      )}
      <button
        type="button"
        onClick={onClose}
        className="inline-flex items-center rounded bg-ink-primary px-4 h-9 text-sm font-medium text-white hover:bg-ink-primary/90"
      >
        Close
      </button>
    </footer>
  );
}

function fmtDate(iso: string): string {
  return fmtDateTime(iso);
}

void PRESIGN_EXPIRY_BUFFER_MS; // reserved for future presigned-URL refresh logic

/**
 * Sprint-18 (eISF spec §edge case — DocuSeal Envelope Expiration) —
 * restart-signing dialog. Operator-triggered when an envelope dies
 * (expired, signers never opened it, DocuSeal silently lost it, etc).
 *
 * Closes the dead routing rows on the BE, then calls /approval/re-initiate
 * which creates fresh envelopes via the same routing rules. Mandatory
 * reason captured for the inspector audit trail.
 */
function RestartSigningDialog({
  binderRef, documentRef, versionId, onClose, onRestarted,
}: {
  binderRef: string;
  documentRef: string;
  versionId: string;
  onClose: () => void;
  onRestarted: () => void;
}) {
  const [workflow, setWorkflow] = useState<
    'Send for PI Signature' | 'Send for Review' | 'Send for eSignature Approval' | 'Send for Read and Understand'
  >('Send for eSignature Approval');
  const [reason, setReason] = useState('');

  const restart = useMutation({
    mutationFn: () =>
      binder.reinitiateApproval(binderRef, documentRef, versionId, {
        workflow_name: workflow,
        reason: reason.trim(),
      }),
    onSuccess: (r) => {
      toast.success(`Restarted — closed ${r.closed_routing_count} dead routing(s), new envelopes dispatched.`);
      onRestarted();
    },
    onError: (err) => toast.error(errorText(err, 'Failed to restart signing')),
  });

  const isValid = reason.trim().length >= 3;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-ink-primary/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-lg p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
            <RotateCw className="h-4 w-4 text-amber-700" />
          </div>
          <div className="flex-1">
            <div className="text-base font-semibold text-ink-primary">Restart signing</div>
            <div className="mt-1 text-sm text-ink-muted">
              Close the dead envelope's routing rows and dispatch fresh DocuSeal
              envelopes via the same routing rules. The audit trail records every
              attempt — inspectors see both the dead envelope and the new one.
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-ink-muted hover:text-ink-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div>
          <label className="label text-xs">Workflow</label>
          <select
            className="input w-full"
            value={workflow}
            onChange={(e) => setWorkflow(e.target.value as typeof workflow)}
          >
            <option>Send for eSignature Approval</option>
            <option>Send for PI Signature</option>
            <option>Send for Review</option>
            <option>Send for Read and Understand</option>
          </select>
          <p className="mt-1 text-[11px] text-ink-muted">
            Keep the same workflow the version was originally on unless the
            recovery requires a different approval flavour.
          </p>
        </div>

        <div>
          <label className="label text-xs">Reason for restart *</label>
          <textarea
            className="input min-h-[80px]"
            value={reason}
            placeholder="DocuSeal envelope expired before PI signed; coordinator confirmed via DocuSeal admin panel."
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
          />
          <p className="mt-1 text-[11px] text-ink-muted">
            Captured verbatim in the audit row. Minimum 3 characters.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-surface-border">
          <button type="button" onClick={onClose} className="btn-ghost" disabled={restart.isPending}>Cancel</button>
          <button
            type="button"
            onClick={() => restart.mutate()}
            disabled={!isValid || restart.isPending}
            className="inline-flex items-center gap-1.5 rounded bg-amber-600 px-3.5 h-9 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-60"
          >
            {restart.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
            Restart signing
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Sprint-18 (eISF spec §3) — check-out conflict dialog.
 *
 * Surfaced when the server 409s on /check-out (another user holds the
 * slot). Shows the server's message verbatim (it carries the holder's
 * name) + a force-release CTA gated on the spec permission matrix
 * (`lock_or_unlock_document` allows Reg Coord / Site Admin / Platform
 * Admin to override).
 *
 * Force-release calls /check-in with force=true on the same document.
 * The audit row stamps `forced_by_role` so inspectors see the override.
 */
function CheckOutConflictDialog({
  message, canForce, onForce, forcePending, onClose,
}: {
  message: string;
  canForce: boolean;
  onForce: () => void;
  forcePending: boolean;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-ink-primary/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
            <Lock className="h-4 w-4 text-amber-700" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-base font-semibold text-ink-primary">Document is checked out</div>
            <div className="mt-1 text-sm text-ink-secondary break-words">{message}</div>
          </div>
          <button type="button" onClick={onClose} className="text-ink-muted hover:text-ink-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        {canForce ? (
          <div className="rounded border border-surface-border bg-surface-muted/30 p-3 text-xs text-ink-muted">
            As a coordinator, you can force-release the check-out. The override is
            audited with your role + the prior holder; the other user will see
            the slot is free next time they refresh.
          </div>
        ) : (
          <div className="rounded border border-surface-border bg-surface-muted/30 p-3 text-xs text-ink-muted">
            Your role can't override another user's check-out. Ask the holder
            to check the document in, or escalate to a site coordinator.
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-surface-border">
          <button type="button" onClick={onClose} className="btn-ghost" disabled={forcePending}>Close</button>
          {canForce && (
            <button
              type="button"
              onClick={onForce}
              disabled={forcePending}
              className="inline-flex items-center gap-1.5 rounded bg-danger-600 px-3.5 h-9 text-sm font-medium text-white hover:bg-danger-700 disabled:opacity-60"
            >
              {forcePending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UnlockKeyhole className="h-3.5 w-3.5" />}
              Force-release check-out
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
